import React, { useState, useEffect, useMemo, useCallback } from "react";
import Select from "react-select";

import useStore from "@/modules/store";
import { getCustomSelectStyles } from "@/modules/common";
import useDarkMode from "@/hooks/useDarkMode";
import {
  CACHE_INVALIDATED_EVENT,
  fetchWithCache,
  shouldRefreshForInvalidation,
} from "@/modules/api_cache";
import { reportPerformanceMetric, startMetricTimer } from "@/modules/performance_metrics";

const GenericSingleSelect = (props) => {
  const { onChange, required, apiPath, storeId, selectedId } = props;
  const params = useMemo(() => props.params || {}, [props.params]);
  const [options, setOptions] = useState([]);
  const currentSelect = useStore((state) => state.selects[storeId]);
  const isInvalid = currentSelect?.invalid;
  const setSelectValue = useStore((state) => state.setSelectValue);
  const isDark = useDarkMode();
  const apiResource = useMemo(() => apiPath.split("/")[0], [apiPath]);

  const fetchOptions = useCallback(async () => {
    const stopTimer = startMetricTimer(
      "select_options_ready_ms",
      { api_resource: apiResource, component: "single_select" },
      "interaction",
    );

    try {
      const data = await fetchWithCache(apiPath, params);
      if (Array.isArray(data)) {
        const formattedOptions = data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setOptions(formattedOptions);
        stopTimer({ option_count: formattedOptions.length });
        return formattedOptions;
      }
      console.error("Invalid data format");
      reportPerformanceMetric(
        "select_options_error",
        1,
        { api_resource: apiResource, component: "single_select", error: "invalid_data" },
        "interaction",
        "counter",
      );
      return [];
    } catch (error) {
      console.error("Error fetching options:", error);
      reportPerformanceMetric(
        "select_options_error",
        1,
        { api_resource: apiResource, component: "single_select", error: "fetch_failed" },
        "interaction",
        "counter",
      );
      return [];
    }
  }, [apiPath, apiResource, params]);

  const updateSelectedOptionFromStore = useCallback(
    (initialSetup = false, currentOptions = options) => {
      if (currentSelect?.value) {
        const isNewOption = currentSelect.value.isNew;
        const selected =
          currentOptions.find((option) => option.value === currentSelect.value.id) ||
          (isNewOption
            ? {
                value: currentSelect.value.id,
                label: currentSelect.value.name,
              }
            : null);

        if (
          isNewOption &&
          selected &&
          !currentOptions.find((option) => option.value === selected.value)
        ) {
          setOptions((prevOptions) => [...prevOptions, selected]);
        }

        if (selected && !initialSetup) {
          if (
            currentSelect.value.id !== selected.value ||
            currentSelect.value.name !== selected.label
          ) {
            setSelectValue(
              storeId,
              { id: selected.value, name: selected.label },
              required,
              false,
            );
          }
        }
      }
    },
    [currentSelect?.value, storeId, required],
  );

  useEffect(() => {
    const initializeSelect = async () => {
      if (!currentSelect) {
        setSelectValue(storeId, null, required, false);
      }

      const fetchedOptions = await fetchOptions();

      if (selectedId && fetchedOptions.length > 0 && !currentSelect?.value) {
        const selectedOption = fetchedOptions.find(
          (option) => option.value === selectedId,
        );
        if (selectedOption) {
          setSelectValue(
            storeId,
            { id: selectedOption.value, name: selectedOption.label },
            required,
            false,
          );
          if (onChange) {
            onChange(selectedOption.value);
          }
        }
      }

      updateSelectedOptionFromStore(true, fetchedOptions);
    };

    initializeSelect();
  }, [storeId, required, fetchOptions, updateSelectedOptionFromStore]);

  useEffect(() => {
    const handleCacheInvalidation = (event) => {
      if (shouldRefreshForInvalidation(apiPath, event.detail?.apiPath)) {
        fetchOptions();
      }
    };

    window.addEventListener(CACHE_INVALIDATED_EVENT, handleCacheInvalidation);
    return () => {
      window.removeEventListener(CACHE_INVALIDATED_EVENT, handleCacheInvalidation);
    };
  }, [apiPath, fetchOptions]);

  useEffect(() => {
    if (options.length > 0) {
      updateSelectedOptionFromStore(false);
    }
  }, [currentSelect?.value, updateSelectedOptionFromStore, options]);

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      setSelectValue(
        storeId,
        { id: selectedOption.value, name: selectedOption.label },
        required,
        false,
      );
      if (onChange) {
        onChange(selectedOption.value);
      }
    } else {
      setSelectValue(storeId, null, required, true);
      if (onChange) {
        onChange(null);
      }
    }
  };

  const customStyles = getCustomSelectStyles(required, isInvalid, isDark);
  const displayPlaceholder = props.placeholder || "请选择...";

  const selectedOption = currentSelect?.value
    ? options.find((option) => option.value === currentSelect.value.id)
    : null;

  return (
    <Select
      value={selectedOption}
      onChange={handleChange}
      options={options}
      styles={customStyles}
      className="span-sm-gray"
      placeholder={displayPlaceholder}
      isClearable={!required}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      maxMenuHeight={240}
    />
  );
};

export default GenericSingleSelect;
